import React, { useEffect, useRef, useState } from "react";
import api, { fmtErr, API } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { IdCard, Upload, Trash2, Check, Loader2, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const TIPI = ["carta d'identità", "passaporto", "patente", "tessera sanitaria", "visura camerale", "antiriciclaggio", "altro"];
const DUE_LATI = ["carta d'identità", "patente", "tessera sanitaria"];
const COL = { valido: "text-emerald-700", in_scadenza: "text-amber-700", scaduto: "text-destructive", senza_scadenza: "text-muted-foreground" };
const LABEL = { valido: "valido", in_scadenza: "in scadenza", scaduto: "scaduto", senza_scadenza: "senza scadenza" };

export default function DocumentiIdentita({ investorId, isAdmin, onDati }) {
  const ref = useRef(null);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tipo, setTipo] = useState("");
  const [lato, setLato] = useState("");
  const [prop, setProp] = useState(null);
  const [scelti, setScelti] = useState({});
  const load = () => api.get(`/investors/${investorId}/documenti`).then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, [investorId]);

  const carica = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f); if (tipo) fd.append("tipo", tipo);
      if (DUE_LATI.includes(tipo) && lato) fd.append("lato", lato);
      const r = await api.post(`/investors/${investorId}/documenti`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Letto: ${r.data.documento.tipo}${r.data.documento.data_scadenza ? ` · scade il ${fmtDate(r.data.documento.data_scadenza)}` : ""}`);
      const diversi = (r.data.proposte || []).filter((p) => p.diverso);
      setProp(diversi.length ? diversi : null);
      setScelti(Object.fromEntries(diversi.map((p) => [p.campo, true])));
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const applica = async () => {
    const campi = Object.fromEntries(prop.filter((p) => scelti[p.campo]).map((p) => [p.campo, p.proposto]));
    try { await api.post(`/investors/${investorId}/applica-dati`, { campi }); toast.success("Anagrafica aggiornata"); setProp(null); onDati?.(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  const rimuovi = async (id) => {
    if (!window.confirm("Rimuovere questo documento?")) return;
    try { await api.delete(`/investors/${investorId}/documenti/${id}`); load(); } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  if (!d) return null;
  return (
    <Card className="p-4 space-y-3" data-testid="investor-documenti">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold flex items-center gap-2"><IdCard className="w-4 h-4" /> Documenti e identità
          <Badge variant={d.stato === "bloccato" ? "destructive" : d.stato === "attenzione" ? "secondary" : "outline"} data-testid="investor-documenti-stato">{d.stato === "bloccato" ? "non in regola" : d.stato === "attenzione" ? "in scadenza" : "in regola"}</Badge></div>
        {isAdmin && <div className="flex items-center gap-2">
          <select className="border rounded-md h-9 px-2 text-sm bg-background" value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="investor-doc-tipo">
            <option value="">Tipo: lo capisce l'AI</option>{TIPI.map((t) => <option key={t} value={t}>{t}</option>)}</select>
          {DUE_LATI.includes(tipo) && <select className="border rounded-md h-9 px-2 text-sm bg-background" value={lato} onChange={(e) => setLato(e.target.value)} data-testid="investor-doc-lato">
            <option value="">Lato: file unico</option><option value="fronte">fronte</option><option value="retro">retro</option></select>}
          <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp" className="hidden" onChange={carica} data-testid="investor-doc-input" />
          <Button size="sm" disabled={busy} onClick={() => ref.current?.click()} data-testid="investor-doc-carica">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Carica documento</Button>
        </div>}
      </div>
      {!!d.bloccanti?.length && <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive flex items-start gap-2" data-testid="investor-documenti-blocchi">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><div>{d.bloccanti.join(" · ")}. La generazione del contratto AIP è bloccata finché non carichi i documenti validi.</div></div>}
      {!!d.avvisi_lati?.length && <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 flex items-start gap-2" data-testid="investor-documenti-lati">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><div>Documento incompleto: {d.avvisi_lati.join(" · ")}.</div></div>}
      {d.documenti.length === 0 && <div className="text-sm text-muted-foreground">Nessun documento caricato. Mancano: {d.mancanti.join(", ") || "—"}.</div>}
      {d.documenti.map((x) => (
        <div key={x.id} className="flex flex-wrap items-center gap-2 text-sm border-b pb-2 last:border-0" data-testid="investor-doc-row">
          <span className="font-medium capitalize">{x.tipo}{x.lato ? ` - ${x.lato}` : ""}</span>
          {!x.completo && x.stato !== "scaduto" && <Badge variant="destructive" className="text-[10px]" data-testid="investor-doc-incompleto">manca il {x.manca}</Badge>}
          {x.numero && <span className="text-xs text-muted-foreground">n. {x.numero}</span>}
          <span className="text-xs text-muted-foreground">{x.ente_rilascio || ""}</span>
          <span className={`text-xs ${COL[x.stato]}`}>{x.data_scadenza ? `scade il ${fmtDate(x.data_scadenza)} · ${LABEL[x.stato]}` : LABEL[x.stato]}</span>
          {x.canale === "portale" && <Badge variant="outline" className="text-[10px]">dal portale</Badge>}
          <div className="ml-auto flex items-center gap-1">
            {x.document_id && <a href={`${API}/documents/${x.document_id}/download`} target="_blank" rel="noreferrer" className="text-primary" title="Apri il file" data-testid="investor-doc-apri"><ExternalLink className="w-4 h-4" /></a>}
            {isAdmin && <button onClick={() => rimuovi(x.id)} className="text-muted-foreground hover:text-destructive" data-testid="investor-doc-elimina"><Trash2 className="w-4 h-4" /></button>}
          </div>
        </div>))}
      {prop && <div className="rounded-md border border-sky-200 bg-sky-50/70 p-3 space-y-2 text-xs" data-testid="investor-doc-proposte">
        <div className="font-semibold text-sky-900">Dati letti dal documento diversi dall'anagrafica — scegli quali applicare</div>
        {prop.map((p) => <label key={p.campo} className="flex items-start gap-2">
          <input type="checkbox" checked={!!scelti[p.campo]} onChange={(e) => setScelti({ ...scelti, [p.campo]: e.target.checked })} data-testid="investor-doc-proposta" />
          <span><b>{p.etichetta}</b>: {p.attuale || "vuoto"} → <b className="text-sky-900">{p.proposto}</b></span></label>)}
        <div className="flex gap-2 pt-1"><Button size="sm" onClick={applica} data-testid="investor-doc-applica"><Check className="w-3.5 h-3.5 mr-1" /> Applica in anagrafica</Button>
          <Button size="sm" variant="outline" onClick={() => setProp(null)}>Ignora</Button></div>
      </div>}
    </Card>
  );
}
